import { Collapse, List, Typography } from 'antd';
import SVGIcon from 'components/SVGIcon/SVGIcon';
import React, { FC, memo, useCallback } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import styled from 'styles/styled-components';
import { Category, NotificationMessage, NotificationMessageNavigationType, ReadStatus } from 'types/schema';
import { getDateText } from 'utils/dateTimeUtils';
import { generateSlugFromID } from 'utils/urls/categoriesPathSegment';
import NotiIcon from './noti.svg';
import SpeakerIcon from './speaker.svg';

const ListInfo = styled.div`
  display: grid;
  flex-grow: 1;
`;

const ListInfoWrapper = styled.div`
  display: flex;
  gap: 8px;
  flex-grow: 1;
  ${ListInfo} {
    margin-right: 8px;
  }
`;

const ListItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex: 1;
  padding-right: 20px;
  cursor: pointer;
  gap: 20px;
`;

const DateTextWrapper = styled.div`
  flex-shrink: 0;
  width: 162px;
  margin-left: -16px;
`;

const DateText = styled.span`
  padding-left: 20px;
  width: 128px;
  flex-shrink: 0;
`;

const RedDotBadge = styled.div`
  width: 10px;
  height: 10px;
  background: #ff4852;
  border-radius: 50%;
  border: 1px solid #ffffff;
  margin-left: 10px;
`;

const Panel = styled(Collapse.Panel)``;

const CollapseElement = styled(Collapse)`
  border: none !important;
  border-radius: unset !important;
  border-bottom: 1px solid rgba(5, 5, 5, 0.06) !important;
  background-color: transparent !important;
  & .ant-collapse-expand-icon {
    position: absolute;
    right: 10px;
  }
  & .ant-collapse-content-box {
    padding: 0 16px !important;
    background: #f5f5f5 !important;
  }
  & .ant-collapse-header {
    padding-left: 0px !important;
  }
  & .ant-collapse-expand-icon {
    top: 50%;
    transform: translateY(-50%);
  }
`;

const SubNotiItemWrapper = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
`;

const SubNotiItem = styled.div`
  position: relative;
  padding: 16px 0 16px 25px;
  flex-grow: 1;
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 1px;
    height: 100%;
    background: #f0f0f0;
  }
  &:after {
    content: '';
    display: block;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    position: absolute;
    top: 24px;
    left: -2.85px;
    background: #d9d9d9;
  }
`;

const SubNotiItemTextbox = styled.div``;

const SubNotiItemTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #424242;
`;

const SubNotiItemContent = styled.div`
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  color: #424242;
`;

const NotiIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: #e9f2ff;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  margin-top: 6px;
  ${({ speaker }) =>
    speaker &&
    `
    background: #DDF2DF !important;
  `}
`;

interface NotificationItemProps extends RouteComponentProps {
  notificationMessage: NotificationMessage;
  buyerId: string;
  storeId: string;
  markNotificationAsRead: (messageId: string) => void;
  categories: Category[];
  lang: string;
}

const NotificationItem: FC<NotificationItemProps> = (props) => {
  const { history, notificationMessage, buyerId, storeId, markNotificationAsRead, categories, lang } = props;
  const isUnread = notificationMessage.readStatus === ReadStatus.NotRead;

  const onClickNotification = useCallback(
    (id: string) => {
      if (isUnread) {
        markNotificationAsRead(id);
      }
      const messageData = notificationMessage?.data;
      if (!messageData) return;
      if (messageData.navType && messageData.navValue) {
        switch (messageData.navType) {
          case NotificationMessageNavigationType.Category:
            const arrayCategory = messageData.navValue?.split('/'); // NavValue có dạng Category/SubCategory

            const category = categories.find((cat: Category) => cat.name === arrayCategory[0]);
            if (!category) history.push(`/market`);
            else {
              if (arrayCategory.length === 1) history.push(`/${generateSlugFromID(category, lang)}`);
              else {
                const subCategory = category.children?.find((subCat: Category) => subCat.name === messageData.navValue);
                if (!subCategory) history.push(`/${generateSlugFromID(category, lang)}`);
                else history.push(`/${generateSlugFromID(subCategory, lang)}`);
              }
            }
            break;
          case NotificationMessageNavigationType.Product:
            history.push(`/products/${messageData.navValue}`);
            break;
          case NotificationMessageNavigationType.Tag:
            history.push(`/market?tags=${messageData.navValue}`);
            break;
          case NotificationMessageNavigationType.Url:
            window.open(`${messageData.navValue}`, '_blank');
            break;
          default:
            break;
        }
        return;
      }

      if (messageData?.orderId) {
        history.push(`/orders/${messageData.orderId}`);
      } else if (messageData?.ffmNoteId) {
        history.push(`/phieu-giao/${messageData?.ffmNoteId}`);
      }
    },
    [buyerId, storeId, isUnread, markNotificationAsRead, notificationMessage],
  );
  if (!notificationMessage?.subMessages?.length || !notificationMessage?.data?.orderId) {
    return (
      <List.Item onClick={() => onClickNotification(notificationMessage.id)}>
        <ListItem>
          <DateText>{getDateText(notificationMessage.createdAt)}</DateText>
          <ListInfoWrapper>
            <NotiIconWrapper speaker>
              <SVGIcon src={SpeakerIcon} />
            </NotiIconWrapper>
            <ListInfo>
              <Typography.Text>{notificationMessage.title}</Typography.Text>
              <Typography.Text type="secondary">{notificationMessage.content}</Typography.Text>
            </ListInfo>
          </ListInfoWrapper>
        </ListItem>
      </List.Item>
    );
  }
  return (
    <CollapseElement>
      <Panel
        header={
          <List.Item>
            <ListItem>
              <DateText>{getDateText(notificationMessage.createdAt)}</DateText>
              <ListInfoWrapper>
                <NotiIconWrapper>
                  <SVGIcon src={NotiIcon} />
                </NotiIconWrapper>
                <ListInfo>
                  <Typography.Text>{notificationMessage.title}</Typography.Text>
                  <Typography.Text type="secondary">{notificationMessage.content}</Typography.Text>
                </ListInfo>
              </ListInfoWrapper>
              {/* {isUnread && <RedDotBadge />} */}
            </ListItem>
          </List.Item>
        }
        key={notificationMessage.id}
      >
        {notificationMessage.subMessages.map((item) => (
          <SubNotiItemWrapper key={item?.id} onClick={() => onClickNotification(notificationMessage.id)}>
            <DateTextWrapper>
              <DateText>{getDateText(item?.createdAt)}</DateText>
            </DateTextWrapper>
            <SubNotiItem key={item?.id}>
              <SubNotiItemTextbox>
                <SubNotiItemTitle>{item?.title}</SubNotiItemTitle>
                <SubNotiItemContent>{item?.content}</SubNotiItemContent>
              </SubNotiItemTextbox>
            </SubNotiItem>
          </SubNotiItemWrapper>
        ))}
      </Panel>
    </CollapseElement>
  );
};

export default withRouter(memo(NotificationItem));
